/* eslint-disable arrow-body-style */
import * as React from "react";
import {Component } from "react";

import "./arrows.scss";
import "./project-process.scss";

import {observer} from "mobx-react";
import {AppState, MessageBoxType} from "../../components/app/app-mobx";

import {Button, Message, Popup, Progress, Step, StepProps} from "semantic-ui-react";

import {Auftragsverwaltung} from "../../stores/auftragsverwaltung-mobx";
import {Project} from "../../models/project";
import {Icon} from "../../core/components/icon/icon";
import {MapViewerConfiguration} from "../../config/map-viewer-configuration";
import {ProcessStatusConfiguration} from "../../config/process-status-configuration";
import {ProcessLog} from "../../models/process-log";
import {ProcessHook} from "../../hooks/process-hooks";
import {ValidationUtils} from "../../utils/validation-utils";
import {PersistContractorHook} from "../../hooks/persist-contractor-hook";
import {ValidateProjectHook} from "../../hooks/validate-project-hook";
import {CleanupPartialOrdersHook} from "../../hooks/cleanup-partial-orders-hook";
import {ProcessLogs} from "./process-logs";
import {CloseProjectHook} from "../../hooks/close-project-hook";

export interface ProjectProcessProps {
    auftragsverwaltung: Auftragsverwaltung;
    appStore: AppState;
    project: Project | null;
    mapViewerConfig: MapViewerConfiguration;
}

@observer
export class ProjectProcess extends Component<ProjectProcessProps, any> {

    constructor(props: ProjectProcessProps) {
        super(props);
        this.state = {
            processLogs: [],
            error: null
        };
        this.handleStep = this.handleStep.bind(this);
    }

    componentDidMount() {
        const {project} = this.props;
        if (project?.id) {
            this.loadProcessLog(project?.id);
        }
    }

    componentDidUpdate(): void {}

    componentWillUnmount(): void {}

    render(): React.ReactElement | null {
        const {processLogs, error} = this.state;
        const {mapViewerConfig, project} = this.props;
        const process = project?.process;

        if (error) {
            return (
                <Message negative>
                    <Message.Header>Fehler beim Laden des Prozesses</Message.Header>
                    <p>{error}</p>
                </Message>
            );
        }

        if (!project || !process) {
            return null;
        }

        const processConfig = process.config;
        if (!processConfig) {
            throw new Error(`No process configuration found for type ${process.type} and version ${process.version}`);
        }

        const currentStatusId = process.statusId;
        // get all states that have a valid transition from the current state
        const transitionStates = process.getValidTransitionStates(currentStatusId);

        const steps = processConfig.states.map((status: ProcessStatusConfiguration) => {

            const isCurrentStatus = currentStatusId === status.id;
            if (status.type === "error" && !isCurrentStatus) {
                // do not show error, unless the error happened
                return null;
            }

            // Requirements for completed steps:
            // - One of the process log entries have this status in "statusFrom" (the state was passed)
            // - The current status ID of the project have to be greater or equal the ID of the state
            //   (otherwise whe are in a subcycle and process passed this status but cycle is started another time)
            const isStepCompleted = currentStatusId >= status.id;
            // processLogs.some((log: ProcessLog) => {
            //     return log?.statusFrom === status.id;
            // })

            // clickable steps have a configured transition and are marked as 'canClick = true'
            const clickable = status.canClick && transitionStates.includes(status.id);

            // show action label as long as step is not completed (if action label is specified)
            const label = !isStepCompleted && status.actionLabel ? status.actionLabel : status.label;

            let css = "";
            if (!status.isClosed) {
                css += "arrow--3 ";
            }
            if (status.canClick) {
                css += "manual ";
            }
            if (clickable) {
                css += "clickable ";
            }
            if (status.type) {
                css += `${status.type} `;
            }
            if (status.cycle) {
                css += `cycle cycle-${status.cycle} `;
            }
            if (status.visibility) {
                css += `${status.visibility} `;
            }

            // TODO: handle multiple logs
            let processLog;
            for (const log of processLogs) {
                if (log.statusFrom === status.id) {
                    processLog = {
                        timestamp: new Intl.DateTimeFormat("de-CH",
                            {year: "numeric", month: "2-digit", day: "2-digit",
                                hour: "2-digit", minute: "2-digit"}).format(log.timeStamp),
                        message: log.message,
                        editor: log.editor
                    };
                }
            }
            return (
                <Step completed={isStepCompleted}
                    key={status.id}
                    active={isCurrentStatus}
                    className={css}
                    status={status.id}
                    onClick={clickable ? this.handleStep : undefined} >
                    <Icon name={status.icon} selected={isStepCompleted}/>
                    <Step.Content>
                        <Step.Title>{label}</Step.Title>
                        <Step.Description>{status.description}</Step.Description>
                        {status.type === "long-running" && isCurrentStatus &&
                            <Progress percent={process.progress > 0 ? process.progress : 20}
                                attached="bottom"
                                indicating
                            />
                        }
                        { processLog !== undefined &&
                            <ProcessLogs logs={processLogs} status={status} mapViewerConfig={mapViewerConfig} />
                        }
                    </Step.Content>
                </Step>
            );
        });

        return (
            <div className="process">
                <div className="process-heading">
                    <h2>Prozess</h2>
                    <div className="process-heading-button">
                        <Popup content="Aktuellen Prozessstand laden"
                            trigger={
                                <Button className={"refresh-process"} circular
                                    onClick={(e: React.MouseEvent) => {
                                        e.preventDefault();
                                        this.refresh();
                                    }}>
                                    <Icon name={"cloud_refresh"} />
                                </Button>
                            } />
                    </div>
                </div>
                <div className="process-steps">
                    <Step.Group fluid vertical>
                        {steps}
                    </Step.Group>
                </div>
            </div>
        );
    }

    private refresh(): void {
        const {auftragsverwaltung, project} = this.props;

        if (!project || !project.id) {
            return;
        }

        const projectId = project.id;

        // reload project, this will then force reloading this component
        auftragsverwaltung.ProjectStore?.loadItem(projectId).then(() => {
            // reload process log
            this.loadProcessLog(projectId);
        });
    }

    private handleStep(event: React.MouseEvent, step: StepProps): void {
        // Update process status
        const {auftragsverwaltung, appStore, project} = this.props;
        const process = project?.process;

        if (!process) {
            console.error("process undefined");
            return;
        }

        // change state
        const currentStatus = process.statusId;
        const newStatus = step.status;

        // check if status is valid
        const transitionStates = process.getValidTransitionStates(currentStatus);
        if (!transitionStates.includes(newStatus)) {
            console.error(`Invalid target status. No transition specified ${currentStatus} > ${newStatus}`);
            return;
        }

        const user = appStore.esriCredential?.userId || "";
        // any hooks to execute?
        this.executeProcessHooks(project, currentStatus, newStatus).then(() =>  {
            // change process status
            process.statusId = newStatus;
            if (process.statusConfig?.isClosed) {
                process.closedOn = new Date();
            }
            auftragsverwaltung.ProjectProcessStore?.updateProcessStatus(process, currentStatus, newStatus, user).then(() => {
                this.refresh();
            });
        });
    }

    private loadProcessLog(projectId: number): void {
        const { auftragsverwaltung } = this.props;
        auftragsverwaltung.ProcessLogStore?.loadItems([projectId]).then((processLogs: ProcessLog[]) => {
            this.setState({
                processLogs
            });
        }).catch((error) => {
            this.setState({
                error
            });
        });
    }

    private executeProcessHooks(project: Project, currentStatus: number, newStatus: number): Promise<any[]> {
        const { auftragsverwaltung, appStore} = this.props;

        return new Promise((resolve, reject) => {

            const process = project?.process;
            if (!process) {
                console.error("Process undefined when executing hooks");
                reject();
                return;
            }
            if (!project?.id) {
                console.error("Project undefined when executing hooks");
                reject();
                return;
            }

            const runningHooks = process.config.hooks.map((config: ProcessHook) => {
                if (!config.transitions.find((trans) => trans[0] === currentStatus && trans[1] === newStatus)) {
                    // this hook is not configured for this status transition
                    return;
                }
                // have to initialize the hook class here. After deserializing the class, all the methods are missing
                // TODO: find out why the functions are undefined (e.g. run()) after deserializing
                let hook: ProcessHook | undefined;
                if (config.id === "CleanupPartialOrdersHook") {
                    hook = new CleanupPartialOrdersHook(config);
                }
                if (config.id === "PersistContractorHook") {
                    hook = new PersistContractorHook(config);
                }
                if (config.id === "CloseProjectHook") {
                    hook = new CloseProjectHook(config);
                }
                if (config.id === "ValidateProjectHook") {
                    hook = new ValidateProjectHook(config);
                }
                if (!hook) {
                    return;
                }
                return hook?.run({process, project, auftragsverwaltung, appStore});

            }).filter(ValidationUtils.notEmpty); // filter null or undefined

            Promise.all(runningHooks)
                .then((values) => {
                    // values is an array of the resolved values
                    resolve(values);
                })
                .catch((error) => {
                    appStore.showMessageBox({
                        title: "Prozessfehler",
                        message: error,
                        type: MessageBoxType.Error
                    });
                    this.refresh();
                });
        });
    }

}
